import axios, { AxiosInstance } from "axios";
import stringify from "fast-json-stable-stringify";
import { ObservableMap } from "mobx";
import { ICache } from "../../../client/stores";
import {
    CoastFeaturedQueryVariables,
    CoastSearchItemFragment,
    GetPwsSearchResultsQueryVariables,
} from "../generated/webapi.g";
import { Cursor } from "../lib/cursor";
import { logger } from "../lib/logging";
import {
    CreateSubscriptionPayload,
    CreateUserPayload,
    PwsCreateUserResponse,
    PwsListSubscriptionPlansResponse,
    PwsLoginResponse,
    PwsOnAirStatusResponse,
    PwsPasswordHintResponse,
    PwsPresentGiftSubscriptionPayload,
    PwsPresentGiftSubscriptionResponse,
    PwsSubmittedResponse,
    PwsSuccessResponse,
    PwsViewBillingInfoResponse,
    PwsViewSubscriptionResponse,
    PwsViewUserResponse,
    ZypeTokenResponse,
} from "./PWS.types";
import { sdk } from "./sdk";

const log = logger.getChild("PWS Service");

const PWS_SITE_SLUG_TO_PROPERTY: Record<string, string> = {
    "coast-pr": "coast",
};

const siteSlugToProperty = (brand: string) => {
    const match = PWS_SITE_SLUG_TO_PROPERTY[brand];

    if (!match) {
        throw new Error(`No PWS Service site match for brand ${brand}`);
    }

    return match;
};

export class PWSMediaService {
    private api: AxiosInstance;
    accessToken?: string;

    constructor(host: string, siteSlug: string) {
        this.api = axios.create({ baseURL: `https://${host}/media/${siteSlugToProperty(siteSlug)}` });
    }

    async getOnAirStatus() {
        const { data } = await this.api.get<PwsOnAirStatusResponse>("/on-air-status");

        return data;
    }
}

export class PWSMemberService {
    private api: AxiosInstance;
    accessToken?: string;

    constructor(host: string, siteSlug: string) {
        this.api = axios.create({ baseURL: `https://${host}/v2/member/${siteSlugToProperty(siteSlug)}` });
    }

    private getBaseHeaders(accessToken?: string) {
        return {
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
            "Content-Type": "application/json",
        };
    }

    async login(username: string, password: string) {
        const { data } = await this.api.post<PwsLoginResponse>("/login", { username, password });

        return data;
    }

    async logout(accessToken: string) {
        const { data } = await this.api.post<PwsLoginResponse>("/logout", null, {
            headers: this.getBaseHeaders(accessToken),
        });

        return data;
    }

    async validateToken(accessToken: string, refreshToken: string | null) {
        let query = "";

        if (refreshToken) {
            query += `?refresh_token=${refreshToken}`;
        }

        const { data } = await this.api.get<ZypeTokenResponse>(`/token${query}`, {
            headers: this.getBaseHeaders(accessToken),
        });

        return data;
    }

    async listSubscriptionPlans() {
        const { data } = await this.api.get<PwsListSubscriptionPlansResponse>("/plans");

        return data;
    }

    async subscribe(accessToken: string, payload: CreateSubscriptionPayload) {
        const { data } = await this.api.post<PwsViewSubscriptionResponse>("/subscriptions", payload, {
            headers: this.getBaseHeaders(accessToken),
        });

        return data;
    }

    async presetGiftSubscription(accessToken: string, payload: PwsPresentGiftSubscriptionPayload) {
        const { data } = await this.api.post<PwsPresentGiftSubscriptionResponse>("/subscriptions/gift", payload, {
            headers: this.getBaseHeaders(accessToken),
        });

        return data;
    }

    async cancelSubscription(accessToken: string, subscriptionId: string) {
        const { data } = await this.api.delete<PwsViewSubscriptionResponse>(`/subscriptions/${subscriptionId}`, {
            headers: this.getBaseHeaders(accessToken),
        });

        return data;
    }

    async reactivateSubscription(accessToken: string, subscriptionId: string) {
        const { data } = await this.api.put<PwsViewSubscriptionResponse>(
            `/subscriptions/${subscriptionId}/reactivate`,
            null,
            {
                headers: this.getBaseHeaders(accessToken),
            },
        );

        return data;
    }

    async upDownGradeSubscription(accessToken: string, subscriptionId: string, nextPlanId: string) {
        const { data } = await this.api.post<PwsViewSubscriptionResponse>(
            `/subscriptions/${subscriptionId}/next-plan/${nextPlanId}`,
            null,
            {
                headers: this.getBaseHeaders(accessToken),
            },
        );

        return data;
    }

    async verifyEmail(email: string, recaptchaToken: string) {
        const { data } = await this.api.post<PwsSubmittedResponse>(`/verify-email/${email}`, null, {
            params: { recaptchaToken },
        });

        return data;
    }

    async getUser(accessToken: string, includeSubscription = true) {
        const { data } = await this.api.get<PwsViewUserResponse>(`/user`, {
            headers: this.getBaseHeaders(accessToken),
            params: { includeSubscription },
        });

        return data;
    }

    async createUser(emailToken: string, user: CreateUserPayload, doLogin = true) {
        const { data } = await this.api.post<PwsCreateUserResponse>(
            "/user",
            { user },
            { params: { emailToken, doLogin } },
        );

        return data;
    }

    async getBillingInfo(accessToken: string) {
        const { data } = await this.api.get<PwsViewBillingInfoResponse>("/billing", {
            headers: this.getBaseHeaders(accessToken),
        });

        return data;
    }

    async updateBillingInfo(accessToken: string, body: any) {
        const { data } = await this.api.put<PwsViewBillingInfoResponse>("/billing", body, {
            headers: this.getBaseHeaders(accessToken),
        });

        return data;
    }

    async forgotPassword(email: string, recaptchaToken: string) {
        const { data } = await this.api.post<PwsSubmittedResponse>(
            `/password/forgot/${email}?recaptchaToken=${recaptchaToken}`,
        );

        return data;
    }

    async resetPassword(password: string, passwordToken: string, passwordHint?: string) {
        const { data } = await this.api.put<PwsSubmittedResponse>("/password/reset/", {
            password,
            passwordToken,
            passwordHint,
        });

        return data;
    }

    async initEmailUpdate(currentEmail: string, newEmail: string, password: string) {
        const { data } = await this.api.post<PwsSubmittedResponse>("/update-email/init/", {
            currentEmail,
            newEmail,
            password,
        });

        return data;
    }

    async completeEmailUpdate(emailToken: string) {
        const { data } = await this.api.put<PwsViewUserResponse>(`/update-email/complete/?emailToken=${emailToken}`);

        return data;
    }

    async updatePassword(email: string, currentPassword: string, newPassword: string, passwordHint?: string) {
        const { data } = await this.api.put<PwsViewUserResponse>("/password/update/", {
            email,
            currentPassword,
            newPassword,
            passwordHint,
        });

        return data;
    }

    async getPasswordHint(email: string, recaptchaToken: string) {
        const { data } = await this.api.get<PwsPasswordHintResponse>(
            `/password/hint/${email}?recaptchaToken=${recaptchaToken}`,
        );

        return data;
    }

    async ensureGiftReceiverAccount(email: string, recaptchaToken: string, accessToken: string) {
        const { data } = await this.api.post<PwsSuccessResponse>(
            `/user/gift/${email}?recaptchaToken=${recaptchaToken}`,
            null,
            {
                headers: this.getBaseHeaders(accessToken),
            },
        );

        return data;
    }
}

export const DEFAULT_SEARCH_LIMIT_PER_CATEGORY = 5;

export const doPWSSearch = (cache: ObservableMap<string, ICache>) => (query: GetPwsSearchResultsQueryVariables) =>
    new Cursor<CoastSearchItemFragment[], GetPwsSearchResultsQueryVariables, CoastSearchItemFragment>(
        query,
        async opts => {
            try {
                const response = await sdk.GetPWSSearchResults.queryAsPromise({
                    ...opts,
                    // regular +1 won't work here because this is the limit **per category**.
                    // and we get merged results back
                    limit: opts.limit || DEFAULT_SEARCH_LIMIT_PER_CATEGORY,
                });
                return response.data?.pws.coastAppContent.search.value.items || [];
            } catch (e) {
                log.debug("Error in loader", e && e.response ? e.response.data.errors : e);
                throw e;
            }
        },
        (results, opts) => {
            if (results.length) {
                const hasMore = results.length >= (opts.limit || DEFAULT_SEARCH_LIMIT_PER_CATEGORY);
                opts.offset = (opts.offset || 0) + (opts.limit || DEFAULT_SEARCH_LIMIT_PER_CATEGORY);
                return { opts, hasMore, newData: results };
            }

            return { opts, hasMore: false, newData: [] };
        },
        opts => `pws-search-${stringify(opts)}`,
        cache,
    );

export const getCoastFeaturedItems = (query: CoastFeaturedQueryVariables = {}) =>
    sdk.CoastFeatured.queryAsPromise(query).then(r => r.data?.pws.coastAppContent.featured.value || []);
